import { createStore } from 'vuex'
import axios from 'axios'
import { ref } from 'vue'
import { useRouter } from 'vue-router'

// 注意：axios 配置已在 main.js 中统一设置，这里不需要重复配置

// 添加请求拦截器
axios.interceptors.request.use(
  config => {
    // 在发送请求之前做些什么
    return config
  },
  error => {
    console.error('Request error:', error)
    return Promise.reject(error)
  }
)

// 添加响应拦截器
axios.interceptors.response.use(
  response => {
    return response
  },
  error => {
    console.error('Response error:', error)
    if (error.response && error.response.status === 401) {
      // 如果是未认证，清除认证状态并跳转到登录页
      // 但要避免在logout过程中重复处理
      if (!error.config.url?.includes('/auth/logout')) {
        store.commit('setAuthenticated', false)
        store.commit('SET_USER', null)
        // 只在有router且当前不在登录页时才跳转
        if (router && window.location.pathname !== '/login') {
          router.push('/login')
        }
      }
    }
    return Promise.reject(error)
  }
)

const store = createStore({
  state: {
    isAuthenticated: localStorage.getItem('isAuthenticated') === 'true',
    user: JSON.parse(localStorage.getItem('user') || 'null'),
    products: [],
    currentProduct: null,
    loading: false,
    error: null,
    imageTypes: null,
    materials: [],
    themeSeries: [],
    printSizes: [],
    productImages: {}
  },
  
  mutations: {
    setAuthenticated(state, value) {
      state.isAuthenticated = value
      localStorage.setItem('isAuthenticated', value)
    },
    SET_USER(state, user) {
      state.user = user
      localStorage.setItem('user', JSON.stringify(user))
    },
    SET_PRODUCTS(state, products) {
      state.products = products
    },
    SET_CURRENT_PRODUCT(state, product) {
      state.currentProduct = product
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    SET_IMAGE_TYPES(state, types) {
      state.imageTypes = types
    },
    SET_MATERIALS(state, materials) {
      state.materials = materials
    },
    SET_THEME_SERIES(state, themeSeries) {
      state.themeSeries = themeSeries
    },
    SET_PRINT_SIZES(state, printSizes) {
      state.printSizes = printSizes
    },
    UPDATE_PRODUCT_IMAGES(state, { productId, images }) {
      if (state.currentProduct && state.currentProduct.id === productId) {
        state.currentProduct.images = images
      }
      const product = state.products.find(p => p.id === productId)
      if (product) {
        product.images = images
      }
    },
    SET_PRODUCT_IMAGES(state, { productId, images }) {
      state.productImages = {
        ...state.productImages,
        [productId]: images
      }
    },
    UPDATE_PRODUCT_IMAGES(state, { productId, color, type, images }) {
      const productImages = state.productImages[productId] || {}
      const colorImages = productImages[color] || {}
      
      state.productImages = {
        ...state.productImages,
        [productId]: {
          ...productImages,
          [color]: {
            ...colorImages,
            [type]: images
          }
        }
      }
    },
    DELETE_PRODUCT_IMAGE(state, { productId, color, type, index }) {
      const productImages = state.productImages[productId] || {}
      const colorImages = productImages[color] || {}
      const typeImages = colorImages[type] || []
      
      typeImages.splice(index, 1)
      
      state.productImages = {
        ...state.productImages,
        [productId]: {
          ...productImages,
          [color]: {
            ...colorImages,
            [type]: typeImages
          }
        }
      }
    }
  },
  
  actions: {
    async login({ commit }, credentials) {
      try {
        const response = await axios.post('/api/auth/login', credentials)
        if (response.data.message === '登录成功') {
          commit('setAuthenticated', true)
          commit('SET_USER', response.data.user)
          commit('SET_ERROR', null)
          // 添加自动刷新token的逻辑
          setTimeout(() => {
            this.dispatch('refreshSession')
          }, 6 * 60 * 60 * 1000) // 6小时后刷新session
        }
        return response.data
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || '登录失败')
        throw error
      }
    },
    
    async refreshSession({ commit }) {
      try {
        const response = await axios.get('/api/auth/check-session')
        if (response.data.authenticated) {
          setTimeout(() => {
            this.dispatch('refreshSession')
          }, 6 * 60 * 60 * 1000) // 再次设置6小时后刷新
        } else {
          commit('setAuthenticated', false)
          commit('SET_USER', null)
        }
      } catch (error) {
        commit('setAuthenticated', false)
        commit('SET_USER', null)
      }
    },
    
    async logout({ commit }) {
      try {
        // 取消所有pending的请求
        if (window.axiosCancelTokens) {
          window.axiosCancelTokens.forEach(cancel => cancel())
          window.axiosCancelTokens = []
        }
        
        // 清除所有定时器
        const timers = window.sessionStorage.getItem('timers')
        if (timers) {
          JSON.parse(timers).forEach(timer => clearTimeout(timer))
          window.sessionStorage.removeItem('timers')
        }

        await axios.post('/auth/logout')
      } finally {
        // 清除所有状态
        commit('setAuthenticated', false)
        commit('SET_USER', null)
        commit('SET_PRODUCTS', [])
        commit('SET_CURRENT_PRODUCT', null)
        commit('SET_MATERIALS', [])
        commit('SET_THEME_SERIES', [])
        commit('SET_PRINT_SIZES', [])
        commit('SET_PRODUCT_IMAGES', {})
        // 清除本地存储
        localStorage.removeItem('isAuthenticated')
        localStorage.removeItem('user')
        // 立即跳转到登录页
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
      }
    },
    
    async changePassword({ commit }, { oldPassword, newPassword }) {
      commit('SET_LOADING', true)
      try {
        const response = await axios.post('/api/auth/change-password', {
          oldPassword,
          newPassword
        })
        return response.data
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || '修改密码失败')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchProducts({ commit }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const response = await axios.get('/api/products/')
        const raw = response.data
        let list = []
        if (Array.isArray(raw)) list = raw
        else if (raw && Array.isArray(raw.data)) list = raw.data
        else if (raw && Array.isArray(raw.items)) list = raw.items
        else if (raw && raw.data && Array.isArray(raw.data.items)) list = raw.data.items
        else throw new Error('Invalid products data format')
        list = list.filter((p) => p && typeof p === 'object')
        commit('SET_PRODUCTS', list)
        return list
      } catch (error) {
        console.error('Error fetching products:', error)
        commit('SET_ERROR', error.response?.data?.message || '获取布料列表失败')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchProduct({ commit }, productId) {
      commit('SET_LOADING', true)
      try {
        const response = await axios.get(`/products/${productId}`)
        commit('SET_CURRENT_PRODUCT', response.data)
      } catch (error) {
        commit('SET_ERROR', error.message)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchImageTypes({ commit }) {
      try {
        const response = await axios.get('/products/image-types')
        commit('SET_IMAGE_TYPES', response.data)
      } catch (error) {
        console.error('Error fetching image types:', error)
      }
    },
    
    async createProduct({ commit }, productData) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const response = await axios.post('/products/', productData)
        return response.data
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || '创建布料失败')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async updateProduct({ commit }, { id, data }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const response = await axios.put(`/products/${id}`, data)
        return response.data
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || '更新布料失败')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async deleteProduct({ commit }, id) {
      commit('SET_LOADING', true)
      try {
        const response = await axios.delete(`/products/${id}`)
        return response.data
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || '删除失败')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async deleteImage({ dispatch }, { productId, imageId }) {
      await axios.delete(`/products/${productId}/images/${imageId}`)
      return dispatch('fetchProduct', productId)
    },
    
    async updateImageOrder({ commit }, { productId, updates }) {
      try {
        const response = await axios.put(`/products/${productId}/images/order`, { updates })
        return response.data
      } catch (error) {
        console.error('Error updating image order:', error)
        throw error
      }
    },
    
    // 获取所有材质选项
    async fetchMaterials({ commit }) {
      try {
        const response = await axios.get('/categories/materials')
        commit('SET_MATERIALS', response.data)
      } catch (error) {
        console.error('获取材质列表失败:', error)
      }
    },
    
    // 获取所有主题系列选项
    async fetchThemeSeries({ commit }) {
      try {
        const response = await axios.get('/categories/theme-series')
        commit('SET_THEME_SERIES', response.data)
      } catch (error) {
        console.error('获取主题系列列表失败:', error)
      }
    },
    
    // 获取所有印制尺寸选项
    async fetchPrintSizes({ commit }) {
      try {
        const response = await axios.get('/categories/print-sizes')
        commit('SET_PRINT_SIZES', response.data)
      } catch (error) {
        console.error('获取印制尺寸列表失败:', error)
      }
    },
    
    // 添加新的材质
    async addMaterial({ commit, state }, material) {
      try {
        const response = await axios.post('/categories/materials', { name: material })
        const newMaterials = [...state.materials, material]
        commit('SET_MATERIALS', newMaterials)
        return response.data
      } catch (error) {
        console.error('添加材质失败:', error)
        throw error
      }
    },
    
    // 添加新的主题系列
    async addThemeSeries({ commit, state }, themeSeries) {
      try {
        const response = await axios.post('/categories/theme-series', { name: themeSeries })
        const newThemeSeries = [...state.themeSeries, themeSeries]
        commit('SET_THEME_SERIES', newThemeSeries)
        return response.data
      } catch (error) {
        console.error('添加主题系列失败:', error)
        throw error
      }
    },
    
    // 添加新的印制尺寸
    async addPrintSize({ commit, state }, printSize) {
      try {
        const response = await axios.post('/categories/print-sizes', { name: printSize })
        const newPrintSizes = [...state.printSizes, printSize]
        commit('SET_PRINT_SIZES', newPrintSizes)
        return response.data
      } catch (error) {
        console.error('添加印制尺寸失败:', error)
        throw error
      }
    },
    
    // 上传产品图片
    async uploadProductImages({ commit }, { productId, color, images }) {
      const formData = new FormData()
      
      images.forEach(image => {
        formData.append('files[]', image)
      })
      
      try {
        const response = await axios.post(
          `/products/${productId}/images/${color}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        )
        
        if (response.data && response.data.images) {
          // 更新产品图片
          commit('UPDATE_PRODUCT_IMAGES', {
            productId,
            images: response.data.images
          })
        }
        
        return response.data
      } catch (error) {
        console.error('上传图片失败:', error)
        throw error
      }
    },
    
    // 获取产品图片
    async fetchProductImages({ commit }, productId) {
      try {
        const response = await axios.get(`/products/${productId}/images`)
        
        if (response.data && response.data.code === 200) {
          commit('SET_PRODUCT_IMAGES', {
            productId,
            images: response.data.data || {}
          })
          return response.data.data
        } else {
          // 如果没有图片数据，设置为空对象
          commit('SET_PRODUCT_IMAGES', {
            productId,
            images: {}
          })
          return {}
        }
      } catch (error) {
        console.error('获取产品图片失败:', error)
        // 出错时也设置为空对象
        commit('SET_PRODUCT_IMAGES', {
          productId,
          images: {}
        })
        throw error
      }
    },
    
    // 删除产品图片
    async deleteProductImage({ commit }, { productId, color, type, index, imageId }) {
      try {
        await axios.delete(`/products/${productId}/images/${imageId}`)
        
        commit('DELETE_PRODUCT_IMAGE', {
          productId,
          color,
          type,
          index
        })
        
        return { success: true }
      } catch (error) {
        console.error('删除产品图片失败:', error)
        throw error
      }
    },
    
    // 更新图片顺序
    async updateImageOrder({ commit }, { productId, color, type, order }) {
      try {
        const response = await axios.put(`/products/${productId}/images/order`, {
          color,
          type,
          order
        })
        
        return response.data
      } catch (error) {
        console.error('更新图片顺序失败:', error)
        throw error
      }
    }
  }
})

// 创建router实例
let router = null
setTimeout(() => {
  router = useRouter()
}, 0)

export default store 